import CommentIcon from "assets/img/icons/comment.svg?react";
import RightIcon from "assets/img/icons/sidebar/right.svg?react";
import { AuthorAvatar } from "components/common/AuthorChip";
import { Avatar, DeleteDiscussionModal, Rows } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { DocumentTextIcon, TrashIcon } from "components/icons/outline";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatRelativeTime } from "lib/relativeTime";
import { trpc } from "lib/trpc";
import { useSnackbar } from "notistack";
import { useMemo, useRef, useState } from "react";
import { ClientDiscussionListEntry } from "@compass/shared/types";
import { DiscussionView } from "./DiscussionView";

export const DiscussionsListTable = ({
    discussions,
    selectedId,
}: {
    discussions: ClientDiscussionListEntry[];
    selectedId?: number | null;
}) => {
    const currentUser = useCurrentUser();
    const timeZone = useUserTimezone();
    const { enqueueSnackbar } = useSnackbar();
    const [activeId, setActiveId] = useState<number | null>(selectedId ?? null);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const queryClient = trpc.useUtils();
    const deleteDiscussion = trpc.discussions.delete.useMutation({
        onSuccess: (_, variables) => {
            if (activeId === variables.discussionId) setActiveId(null);
            void queryClient.discussions.list.invalidate();
            enqueueSnackbar("Discussion deleted.", { variant: "success" });
        },
        onError: () => enqueueSnackbar("Failed to delete discussion.", { variant: "error" }),
    });
    const notifiedMissingRef = useRef(false);

    const selectedDiscussion = useMemo(() => {
        if (activeId == null) return null;
        const match = discussions.find((d) => d.id === activeId);
        if (match) return match;
        // Only show missing notification for URL-based selection, not user clicks
        if (
            selectedId != null &&
            activeId === selectedId &&
            discussions.length > 0 &&
            !notifiedMissingRef.current
        ) {
            notifiedMissingRef.current = true;
            enqueueSnackbar("This discussion has been deleted or is no longer available.", {
                variant: "info",
            });
        }
        return null;
    }, [activeId, selectedId, discussions, enqueueSnackbar]);

    return (
        <div className="flex gap-4 pt-4">
            {/* Discussion list */}
            <div
                className={`transition-all duration-300 ${selectedDiscussion ? "w-1/3" : "w-full"}`}
            >
                <Rows className="space-y-2">
                    {discussions.map((discussion) => {
                        const lastComment = discussion.lastComment;
                        const isLastMessageFromOtherUnread =
                            lastComment && !lastComment.author.isCurrentUser && !lastComment.isRead;

                        return (
                            <div
                                key={discussion.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setActiveId(discussion.id)}
                                onKeyDown={(e) => e.key === "Enter" && setActiveId(discussion.id)}
                                className="block w-full cursor-pointer text-left focus:rounded-lg focus:outline-hidden"
                            >
                                <div
                                    className={cn(
                                        "flex flex-row items-start justify-between rounded-2xl border border-[#c8c8c8] px-4 py-4 transition-all duration-300 focus:outline-hidden",
                                        {
                                            "bg-[#f2f2f2]":
                                                selectedDiscussion?.id === discussion.id,
                                            "bg-[#DFF1FA] hover:bg-[#DFF1FA]":
                                                isLastMessageFromOtherUnread &&
                                                selectedDiscussion?.id !== discussion.id,
                                            "bg-white hover:bg-gray-100":
                                                !isLastMessageFromOtherUnread &&
                                                selectedDiscussion?.id !== discussion.id,
                                        },
                                    )}
                                >
                                    <div className="flex h-full flex-row items-start justify-between gap-3">
                                        {lastComment ? (
                                            <AuthorAvatar
                                                author={lastComment.author}
                                                surface="private"
                                                className="h-10 w-10 text-sm"
                                                target="_blank"
                                            />
                                        ) : (
                                            <Avatar
                                                photoUrl={null}
                                                className="h-10 w-10 text-sm"
                                                name="No comments yet"
                                            />
                                        )}
                                        <div className="space-y-3 flex flex-col">
                                            <div className="text-sm font-semibold text-gray-900">
                                                {discussion.title}
                                            </div>
                                            <div className="flex flex-row gap-3 text-xs text-gray-500">
                                                <div className="flex flex-row items-center gap-1">
                                                    <CommentIcon className="h-[16px] w-[16px]" />
                                                    <span>{discussion.commentsCount}</span>
                                                </div>
                                                <div className="flex flex-row items-center gap-1">
                                                    <DocumentTextIcon className="h-[16px] w-[16px]" />
                                                    <span>{discussion.attachmentsCount}</span>
                                                </div>
                                            </div>

                                            {/* organizations */}
                                            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                                {discussion.organizations?.map((org) => (
                                                    <div key={org.id} className="group relative">
                                                        {/* Organization badge */}
                                                        <span className="cursor-pointer rounded bg-gray-100 px-2 py-0.5 text-gray-700 group-hover:bg-gray-200">
                                                            {org.name}
                                                        </span>

                                                        {/* Tooltip with users */}
                                                        {org.users.length > 0 && (
                                                            <div className="absolute top-full left-0 z-10 mt-1 hidden w-56 rounded-md border bg-white p-2 shadow-lg group-hover:block">
                                                                {org.users.map((user, i) => (
                                                                    <div
                                                                        key={
                                                                            user.type ===
                                                                            "identified"
                                                                                ? user.id
                                                                                : `deleted-${i}`
                                                                        }
                                                                        className="flex items-center gap-2 py-1"
                                                                    >
                                                                        <Avatar
                                                                            photoUrl={
                                                                                user.avatarUrl
                                                                            }
                                                                            name={user.name}
                                                                            className="h-6 w-6"
                                                                        />
                                                                        <span
                                                                            className={`text-xs text-gray-700 ${
                                                                                user.type ===
                                                                                    "identified" &&
                                                                                !user.isPrivateEnabled
                                                                                    ? "italic"
                                                                                    : ""
                                                                            }`}
                                                                        >
                                                                            {user.name}
                                                                            {user.type ===
                                                                                "identified" &&
                                                                                !user.isPrivateEnabled &&
                                                                                " (inactive)"}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4 flex flex-col items-end">
                                        <div className="flex flex-col items-end gap-1">
                                            <div className="text-sm font-semibold text-brand-black1">
                                                {isLastMessageFromOtherUnread && (
                                                    <span className="mr-2 rounded-md border border-brand-azure px-1 py-0.5 text-[11px] font-medium text-brand-black1">
                                                        New
                                                    </span>
                                                )}
                                                {lastComment ? "Last comment" : "No comments yet"}
                                            </div>
                                            {lastComment && (
                                                <span className="text-sm font-medium text-brand-black1">
                                                    {formatRelativeTime(
                                                        lastComment.createdAt,
                                                        timeZone,
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {currentUser?.isCompassUser && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteTargetId(discussion.id);
                                                    }}
                                                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                                                    title="Delete discussion"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            )}
                                            <FavoriteButton
                                                target={{ type: "discussion", id: discussion.id }}
                                                size={16}
                                                showCount={false}
                                            />
                                            <RightIcon className="h-5 w-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Rows>
            </div>

            {selectedDiscussion && (
                <div className="w-2/3">
                    <DiscussionView
                        key={selectedDiscussion.id}
                        discussion={selectedDiscussion}
                        onClose={() => setActiveId(null)}
                    />
                </div>
            )}

            <DeleteDiscussionModal
                isOpen={deleteTargetId !== null}
                setIsOpen={(isOpen) => {
                    if (!isOpen) setDeleteTargetId(null);
                }}
                onDelete={() => {
                    if (deleteTargetId !== null) {
                        deleteDiscussion.mutate({ discussionId: deleteTargetId });
                    }
                }}
            />
        </div>
    );
};
